import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Edit, Delete, Search, Close } from "@mui/icons-material";
import AppTheme from "../components/shared-theme/AppTheme";
import { usePositions } from "../hooks/usePositions";
import Position from "../types/Position";

export default function DefinitionsPositions(props: {
  disableCustomTheme?: boolean;
}) {
  const {
    positions,
    loading,
    error,
    addPosition,
    updatePosition,
    deletePosition,
  } = usePositions();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position>({
    positionId: "",
    name: "",
    summary: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [positionToDelete, setPositionToDelete] = useState<Position | null>(
    null
  );
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    if (error) {
      setSnackbarOpen(true);
    }
  }, [error]);

  const filteredSorted = React.useMemo(() => {
    const f = positions.filter((p) =>
      p.name.toLowerCase().includes(search.toLowerCase())
    );
    return f
      .slice()
      .sort((a, b) =>
        order === "asc"
          ? a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
          : b.name.localeCompare(a.name, undefined, { sensitivity: "base" })
      );
  }, [positions, search, order]);

  const handleOpenDialog = (position?: Position) => {
    setEditingPosition(position || { positionId: "", name: "", summary: "" });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPosition({ positionId: "", name: "", summary: "" });
  };

  const handleSave = async () => {
    if (editingPosition.positionId) {
      await updatePosition(
        editingPosition.positionId,
        editingPosition.name,
        editingPosition.summary
      );
    } else {
      await addPosition(editingPosition.name, editingPosition.summary);
    }
    handleCloseDialog();
  };

  const confirmDelete = (position: Position) => {
    setPositionToDelete(position);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (positionToDelete) {
      await deletePosition(positionToDelete.positionId);
      setDeleteDialogOpen(false);
      setPositionToDelete(null);
    }
  };

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <Box p={4}>
        <Card>
          <CardContent>
            <Box sx={{ mb: 2.5 }}>
              {/* header row */}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                sx={{
                  pb: 2,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Typography
                  component="h2"
                  variant="overline"
                  sx={{
                    color: "text.secondary",
                    letterSpacing: 0.8,
                    fontWeight: 700,
                  }}
                >
                  Positions
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Position
                </Button>
              </Stack>

              {/* search row */}
              <TextField
                size="small"
                placeholder="Search positions…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mt: 2, width: "100%" }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearch("")}
                          aria-label="Clear"
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />
            </Box>

            {loading ? (
              <Box
                sx={{
                  height: "350px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : positions.length === 0 ? (
              <Box
                sx={{
                  height: "350px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography align="center" color="textSecondary">
                  No positions available.
                </Typography>
              </Box>
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active
                        direction={order}
                        onClick={() =>
                          setOrder((o) => (o === "asc" ? "desc" : "asc"))
                        }
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredSorted.map((pos) => (
                    <TableRow key={pos.positionId}>
                      <TableCell>{pos.name}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          sx={{ marginRight: "5px" }}
                          onClick={() => handleOpenDialog(pos)}
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => confirmDelete(pos)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add / Edit Position Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          slotProps={{
            paper: {
              component: "form",
              onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                handleCloseDialog();
              },
              sx: {
                backgroundImage: "none",
                width: "400px",
                maxWidth: "100%",
              },
            },
          }}
        >
          <DialogTitle>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                letterSpacing: 0.8,
                fontWeight: 700,
              }}
            >
              {editingPosition.positionId ? "Edit Position" : "Add Position"}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth>
              <FormLabel htmlFor="positionName">Position Name</FormLabel>
              <TextField
                value={editingPosition.name}
                placeholder="Position name"
                onChange={(e) =>
                  setEditingPosition({
                    ...editingPosition,
                    name: e.target.value,
                  })
                }
              />
            </FormControl>
            <FormControl fullWidth>
              <FormLabel htmlFor="summary">Professional Summary</FormLabel>
              <TextField
                multiline
                minRows={4}
                placeholder="Professional Summary"
                value={editingPosition.summary}
                onChange={(e) =>
                  setEditingPosition({
                    ...editingPosition,
                    summary: e.target.value,
                  })
                }
              />
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSave} variant="contained">
              Save
            </Button>
          </DialogActions>
        </Dialog>
        {/* Delete Position dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete{" "}
              <strong>{positionToDelete?.name}</strong>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity="warning"
            onClose={() => setSnackbarOpen(false)}
            sx={{ width: "100%" }}
          >
            {error}
          </Alert>
        </Snackbar>
      </Box>
    </AppTheme>
  );
}
